// Log time: the dialog pages open from their own Log time buttons. What it
// fills in depends on where you are: an open work item fills its project and item,
// a project view the project, My Work and All Work ask for a project with your
// last one filled in. Editing an entry says where its hours move.
import {
  Button, Checkbox, DatePicker, DetailPanel, Field, InlineMessage, Input, Modal, ModalContent, OptionSelect, Textarea,
} from '@olytic/foundation';
import {
  createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { monthLabel, monthOf, moveText, todayIso, hoursText } from './model';
import {
  createTime, deleteTime, fetchBootstrap, fetchItems, fetchTimePeriod, type TimeEntry, updateTime, type WorkBootstrap, type WorkItem,
} from './workApi';

/** Where the page is, for Log time's defaults. */
export type LogTimeContext =
  | { kind: 'item'; projectId: string; itemId: string; label: string }
  | { kind: 'project'; projectId: string; label: string }
  | { kind: 'cross'; label: string };

interface LogTimeApi {
  setContext: (ctx: LogTimeContext | null) => void;
  open: (entry?: TimeEntry) => void;
  /** Bumps after every save or delete, so pages can reload. */
  savedAt: number;
}

const Ctx = createContext<LogTimeApi>({ setContext: () => {}, open: () => {}, savedAt: 0 });

const LAST_PROJECT = 'mothership.work.lastLogProject';
const remembered = () => {
  try {
    return window.localStorage.getItem(LAST_PROJECT);
  } catch {
    return null;
  }
};
const remember = (id: string) => {
  try {
    window.localStorage.setItem(LAST_PROJECT, id);
  } catch {
    // Private windows: nothing to remember with.
  }
};

export function LogTimeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [ctx, setCtx] = useState<LogTimeContext | null>(null);
  const [editing, setEditing] = useState<{ entry: TimeEntry | null } | null>(null);
  const [savedAt, setSavedAt] = useState(0);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const api = useMemo<LogTimeApi>(() => ({
    setContext: setCtx,
    open: (entry) => setEditing({ entry: entry ?? null }),
    savedAt,
  }), [savedAt]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {editing ? (
        <LogTimeDialog
          context={ctxRef.current}
          entry={editing.entry}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setSavedAt(Date.now());
          }}
        />
      ) : null}
    </Ctx.Provider>
  );
}

export const useLogTime = () => useContext(Ctx);

/** Tells Log time where the page is while it is mounted. */
export function useLogTimeContext(ctx: LogTimeContext | null): void {
  const { setContext } = useContext(Ctx);
  const key = JSON.stringify(ctx);
  useEffect(() => {
    setContext(ctx);
    return () => setContext(null);
  }, [key, setContext]);
}

interface Form {
  entry_date: string;
  hours: string;
  project_id: string;
  work_item_id: string;
  owner_id: string;
  billable: boolean;
  notes: string;
}

function LogTimeDialog({
  context, entry, onClose, onSaved,
}: { context: LogTimeContext | null; entry: TimeEntry | null; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [boot, setBoot] = useState<WorkBootstrap | null>(null);
  const [items, setItems] = useState<WorkItem[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [period, setPeriod] = useState<{ owner_hours: number; project_hours: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    let live = true;
    fetchBootstrap(authenticatedFetch)
      .then((b) => {
        if (!live) return;
        setBoot(b);
        const me = b.me?.id ?? '';
        if (entry) {
          setForm({
            entry_date: entry.entry_date, hours: String(entry.hours), project_id: entry.project_id, work_item_id: entry.work_item_id ?? '',
            owner_id: entry.owner_id, billable: entry.billable, notes: entry.notes ?? '',
          });
          return;
        }
        const last = remembered();
        const project = context && context.kind !== 'cross'
          ? context.projectId
          : last && b.projects.some((p) => p.id === last) ? last : '';
        setForm({
          entry_date: todayIso(), hours: '', project_id: project, work_item_id: context?.kind === 'item' ? context.itemId : '',
          owner_id: me, billable: true, notes: '',
        });
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load your projects.'));
    return () => {
      live = false;
    };
  }, [context, entry]);

  const projectId = form?.project_id ?? '';
  useEffect(() => {
    let live = true;
    if (!projectId) {
      setItems([]);
      return undefined;
    }
    fetchItems(authenticatedFetch, { project: projectId })
      .then((list) => live && setItems(list))
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
  }, [projectId]);

  const month = form ? monthOf(form.entry_date || todayIso()) : '';
  useEffect(() => {
    if (!form?.owner_id || !month) return undefined;
    let live = true;
    const t = setTimeout(() => {
      fetchTimePeriod(authenticatedFetch, { owner: form.owner_id, month, project: form.project_id || undefined, exclude: entry?.id })
        .then((p) => live && setPeriod(p))
        .catch(() => live && setPeriod(null));
    }, 150);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [form?.owner_id, form?.project_id, month, entry?.id]);

  const set = (patch: Partial<Form>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const me = boot?.me ?? null;
  const full = me?.access !== 'team';
  const person = useCallback((id: string) => boot?.people.find((p) => p.id === id)?.name ?? 'Someone', [boot]);
  const project = boot?.projects.find((p) => p.id === form?.project_id);

  const projects = (boot?.projects ?? []).filter((p) => p.status === 'active' || p.id === form?.project_id);
  const owners = useMemo(() => {
    if (!boot || !form) return [];
    const team = form.project_id ? boot.members.filter((m) => m.project_id === form.project_id).map((m) => m.app_user_id) : [];
    const ids = new Set(full ? team : [me?.id ?? '']);
    if (me && full) ids.add(me.id);
    ids.add(form.owner_id);
    return [...ids].filter(Boolean).map((id) => ({ value: id, label: person(id) }));
  }, [boot, form, full, me, person]);

  const save = async () => {
    if (!form) return;
    const hours = Number(form.hours.replace(',', '.'));
    if (!form.project_id) return setError('Choose a project.');
    if (!(hours > 0) || hours > 24) return setError('Enter between 0.25 and 24 hours.');
    if (!form.entry_date) return setError('Choose a date.');
    setSaving(true);
    setError(null);
    const body = {
      entry_date: form.entry_date, hours, project_id: form.project_id, work_item_id: form.work_item_id || null,
      owner_id: form.owner_id, billable: form.billable, notes: form.notes.trim() || null,
    };
    try {
      if (entry) await updateTime(authenticatedFetch, entry.id, body);
      else await createTime(authenticatedFetch, body);
      remember(form.project_id);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not log the time.');
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!entry) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    try {
      await deleteTime(authenticatedFetch, entry.id);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the time entry.');
      setSaving(false);
    }
  };

  const hours = Number(form?.hours.replace(',', '.') ?? 0) || 0;
  const loggedBy = entry?.created_by_id ?? me?.id ?? '';
  const moved = entry && form && boot
    ? moveText(entry, { entry_date: form.entry_date || entry.entry_date, owner_id: form.owner_id, project_id: form.project_id || entry.project_id }, {
        person,
        project: (id) => boot.projects.find((p) => p.id === id)?.code ?? 'the project',
      })
    : null;
  const contextLine = entry
    ? 'Editing a time entry'
    : context?.kind === 'cross'
      ? `You’re looking at ${context.label}, so pick a project${form?.project_id ? '. Your last one is filled in.' : '.'}`
      : context ? `From ${context.label}` : '';

  const itemOptions = items
    .filter((i) => i.type !== 'epic' && (!i.archived_at || i.id === form?.work_item_id))
    .sort((a, b) => Number(a.status === 'done') - Number(b.status === 'done') || a.number - b.number)
    .map((i) => ({ value: i.id, label: `${i.key} · ${i.title}${i.status === 'done' ? ' (done)' : ''}` }));

  return (
    <Modal open onOpenChange={(open) => (open ? undefined : onClose())}>
      <ModalContent
        size="md"
        title={entry ? 'Edit time' : 'Log time'}
        description={contextLine || undefined}
        footer={
          <>
            {entry ? (
              <Button variant="destructive-subtle" className="mr-auto" onClick={remove} disabled={saving}>
                {confirmDelete ? 'Click again to delete' : 'Delete entry'}
              </Button>
            ) : null}
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="brand" onClick={save} loading={saving} disabled={!form}>{entry ? 'Save changes' : 'Log time'}</Button>
          </>
        }
      >
        {!form ? (
          error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : <div className="py-6 text-center text-sm text-[var(--fg-muted)]">Loading…</div>
        ) : (
          <form
            className="grid grid-cols-1 gap-3.5 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <Field label="Date">
              <DatePicker value={form.entry_date} onChange={(next) => set({ entry_date: next })} required />
            </Field>
            <Field label="Hours">
              <Input
                type="number" min={0.25} max={24} step={0.25} inputMode="decimal" placeholder="0.0" autoFocus={Boolean(form.project_id)}
                value={form.hours} onChange={(e) => set({ hours: e.target.value })}
              />
            </Field>
            <Field label="Project" required className="sm:col-span-2">
              <OptionSelect
                options={projects.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))}
                value={form.project_id}
                placeholder="Choose a project…"
                onValueChange={(value) => {
                  const ownerOk = boot?.members.some((m) => m.project_id === value && m.app_user_id === form.owner_id) || full;
                  set({ project_id: value, work_item_id: '', owner_id: ownerOk ? form.owner_id : me?.id ?? form.owner_id });
                }}
              />
            </Field>
            <Field label="Work Item (Optional)" className="sm:col-span-2">
              <OptionSelect
                options={itemOptions}
                value={form.work_item_id}
                emptyLabel="None: bill to the project"
                disabled={!form.project_id}
                onValueChange={(value) => set({ work_item_id: value })}
              />
            </Field>
            <Field label="Time Owner">
              <OptionSelect options={owners} value={form.owner_id} disabled={!full || !form.project_id} onValueChange={(value) => set({ owner_id: value })} />
            </Field>
            <label className="flex items-center gap-2 pb-2 text-sm text-[var(--fg)] pt-6">
              <Checkbox checked={form.billable} onCheckedChange={(v) => set({ billable: v === true })} />
              Billable
            </label>
            <Field label="Notes" className="sm:col-span-2">
              <Textarea rows={2} placeholder="What did you work on?" value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
            </Field>
            <div className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface-header)] px-3 py-2.5 sm:col-span-2">
              <DetailPanel
                columns={1}
                fields={[
                  {
                    key: 'account',
                    label: 'Account',
                    value: project ? <>{project.account_name} <span className="text-[var(--fg-muted)]">· from the project</span></> : <span className="text-[var(--fg-muted)]">Filled in from the project</span>,
                  },
                  {
                    key: 'by',
                    label: 'Logged by',
                    value: <>{person(loggedBy)}{loggedBy !== form.owner_id ? <span className="text-[var(--fg-muted)]"> · for {person(form.owner_id)}</span> : null}</>,
                  },
                  {
                    key: 'period',
                    label: 'Counts Toward',
                    value: (
                      <span>
                        {person(form.owner_id)} · {monthLabel(month)}
                        {period ? (
                          <span className="block tabular-nums text-[var(--fg-muted)]">
                            {hoursText(period.owner_hours)} h → <span className="font-emphasis text-[var(--fg-strong)]">{hoursText(period.owner_hours + hours)} h</span> logged that month
                            {project ? `, ${hoursText(period.project_hours)} → ${hoursText(period.project_hours + hours)} h on ${project.code}` : ''}
                          </span>
                        ) : null}
                      </span>
                    ),
                  },
                ]}
              />
              {moved ? <p className="m-0 mt-2 border-0 border-t border-solid border-[var(--border)] pt-2 text-xs text-[var(--accent-strong)]">{moved}</p> : null}
            </div>
            {error ? <InlineMessage tone="error" role="alert" className="sm:col-span-2">{error}</InlineMessage> : null}
            <button type="submit" hidden aria-hidden tabIndex={-1} />
          </form>
        )}
      </ModalContent>
    </Modal>
  );
}
